import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import * as routerActions from 'connected-react-router'
import { parse } from 'querystringify'
import cn from 'clsx'

// Actions
import { actions as payActions } from 'modules/pay/actions'
import { actions as userSongActions } from 'modules/usersongs/actions'
import { actions as profileActions } from 'modules/profile/actions'

// Components
import { Toast, Error, Button, IconExclamation, IconCheckmarkFilled, Spinner } from '@tracklib/kit'
import { PaymentMethod } from 'components/subscribe/subscribeconfirm'
import { SuccessBox, PaymentError } from 'components/pay/paymentstatus'
import { Summary } from 'kit'
import CheckoutLayout from 'components/pay/checkoutlayout'
import Details from 'components/licensing/details'
import PayButton from 'components/pay/paybutton'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useLocation } from 'react-router'
import { useSavedPaymentMethod } from 'modules/pay/hooks'

// Selectors
import { licenseInfoSelector, LicenseSelectorInfo } from 'selectors'
import { selectLatestLicenseVersion } from 'modules/licensing/selectors'
import { selectUserSongWithSongs } from 'modules/usersongs/selectors'

// Types
import { Dispatch, LicenseVersionWithLicenseTypes, State, SampleLicenseOrder, UserSong, Song } from 'types'

// Utils
import { isBrowser } from '@process'
import { TRACKLIB_CONTACT_SUPPORT_PAGE } from '@constants'

import styles from './licensepay.css'

export const BLOCKED_SONG_IDS: Array<number> = []

const checklist = [
  { completed: true, description: 'Provide us with the final title, length, and song genre' },
  { completed: false, description: 'Upload an mp3 file of the new song to us' },
  { completed: false, description: 'Register and confirm the works registration of your new song' },
  { completed: false, description: 'Confirm the recording registration of your new song' },
  { completed: false, description: 'Tell us how you distributed it and give us the ISRC' },
]

const LicenseInfoChecklist = () => {
  return (
    <div className={styles.checklistWrapper}>
      <div className={styles.checklistText}>
        <strong>Before your license is activated, you must do the following</strong>
      </div>
      {checklist.map(item => (
        <div className={styles.listItem} key={item.description}>
          <div className={styles.iconWrapper}>
            <IconCheckmarkFilled checked={item.completed} />
          </div>
          <span className={cn(item.completed ? styles.complete : styles.todo)}>{item.description}</span>
        </div>
      ))}
      <div className={styles.checklistText}>Don’t worry if this seems hard, we’re here to guide you through each step</div>
    </div>
  )
}

const LicenseInfoBox = ({ userSongId }: { userSongId: number }) => {
  return (
    <div className={styles.licenseInfoBox}>
      <SuccessBox
        buttonTo={`/yoursongs/${userSongId && userSongId}/license/`}
        buttonText="Continue to license details"
        buttonDatatesting="button_complete_license"
        title="You cannot release your song yet"
        description="For your license to be valid you need to register it properly and provide us with some information before your new song is released"
        iconType="warning"
        auxiliary={<LicenseInfoChecklist />}
      />
    </div>
  )
}

type LicensePayProps = {
  paymentId: number
}

const LicensePay = (props: LicensePayProps): JSX.Element => {
  const { paymentId } = props
  const dispatch = useDispatch() as Dispatch
  const profile = useProfile()
  const { search } = useLocation()
  const { step } = parse(search) as { step: string }
  const savedPaymentMethod = useSavedPaymentMethod()
  const sampleLicenseOrder = useSelector<State, SampleLicenseOrder>(state => state.entities.sampleLicenseOrders[String(paymentId)])
  const latestLicenseVersion = useSelector<State, LicenseVersionWithLicenseTypes>(state => selectLatestLicenseVersion(state))
  const userSong = useSelector<State, UserSong>(
    state => sampleLicenseOrder && (selectUserSongWithSongs(state, sampleLicenseOrder.user_song) as UserSong),
  )
  const sample = userSong && userSong.samples[0]
  const licenseInfo = useSelector<State, LicenseSelectorInfo>(state => licenseInfoSelector(state, sample) || ({} as LicenseSelectorInfo))

  const updateUserSongRequest = useRequest('UPDATE_USER_SONG', sampleLicenseOrder?.user_song)
  const updateProfileRequest = useRequest('PROFILE_UPDATE', profile.id)
  const fetchProfileRequest = useRequest('FETCH_PROFILE')
  const fetchSampleLicenseOrderRequest = useRequest('FETCH_SAMPLE_LICENSE_ORDER')
  const paySampleLicenseOrderRequest = useRequest('PAY_SAMPLE_LICENSE_ORDER')
  const isLoading = fetchProfileRequest.isLoading || fetchSampleLicenseOrderRequest.isLoading
  useEffect(() => {
    if (!sampleLicenseOrder) {
      dispatch(payActions.fetchSampleLicenseOrder(paymentId))
    }
  }, [sampleLicenseOrder, dispatch, paymentId])

  const [hasError, setHasError] = useState(false)
  const [licensee, setLicensee] = useState('')
  const [phone, setPhone] = useState('')

  // Catch payment errorsr
  useEffect(() => {
    if (paySampleLicenseOrderRequest.hasError) {
      setHasError(true)
    }
  }, [paySampleLicenseOrderRequest.hasError])

  // Set initial local phone state
  useEffect(() => {
    if (!phone && profile.phone_number) {
      setPhone(profile.phone_number)
    }
  }, [profile]) // eslint-disable-line
  const licenseOrderId = userSong?.samples[0].license_order

  // Set initial licensee licensee
  useEffect(() => {
    if (!licensee && userSong?.licensee) {
      setLicensee(userSong.licensee)
    }
  }, [userSong?.licensee]) // eslint-disable-line

  // This effect used for redirect if changes made to user song as new payment is created on changes made
  useEffect(() => {
    if (userSong && +paymentId !== licenseOrderId) {
      dispatch(routerActions.push(`/pay/license/${licenseOrderId}/`))
    }
  }, [dispatch, userSong, paymentId, licenseOrderId])

  const { state, currency, total_amount, vat_amount, vat_rate } = sampleLicenseOrder || {}
  const hasUpToDateLicenseSampleType = userSong?.samples.some(({ license_sample_type }) =>
    latestLicenseVersion?.sample_types.some(type => type.id === license_sample_type),
  )
  let mainContent = null
  if (isLoading || !isBrowser) {
    mainContent = <Spinner minHeight={300} delay={0} />
  } else if (!sampleLicenseOrder) {
    return <Error message="Not found" />
  } else if (hasError) {
    mainContent = (
      <div className={styles.errorWrapper}>
        <PaymentError button={<Button onClick={() => setHasError(false)}>Try again</Button>} />
      </div>
    )
  } else if (state === 'PAID') {
    mainContent = (
      <>
        <div className={styles.toastWrapper}>
          <Toast title="Your payment was successful" type="positive" centered />
        </div>

        <LicenseInfoBox userSongId={userSong.id} />
      </>
    )
  } else if (BLOCKED_SONG_IDS.includes((sample?.song as Song)?.id)) {
    mainContent = (
      <div>
        <SuccessBox
          title="This license is under review"
          description="Don’t worry, we will solve this issue soon. You can still license this song with the standard revenue share right now by contacting us."
          iconType="warning"
          buttonText="Contact us"
          buttonTo={TRACKLIB_CONTACT_SUPPORT_PAGE}
          buttonExternal
        />
      </div>
    )
  } else if (!hasUpToDateLicenseSampleType && userSong) {
    mainContent = (
      <div className={styles.mismatchSampleLength}>
        <IconExclamation />
        <h2>Please review your license</h2>
        <p>{`One or more of your samples for ${userSong.song_title} needs to be updated. Please click the button below to review your license.`}</p>
        <Button to="usersong" query={{ userSongId: userSong.id, changed: true }} modal>
          Review license
        </Button>
      </div>
    )
  } else if (userSong && (step !== 'pay' || !userSong?.licensee || !profile.phone_number)) {
    mainContent = (
      <Details
        song={sample?.song as Song}
        userSong={userSong}
        licenseInfo={licenseInfo}
        licensee={licensee}
        phone={phone}
        isLoading={updateUserSongRequest.isLoading || updateProfileRequest.isLoading || fetchProfileRequest.isLoading}
        isDone={updateUserSongRequest.isDone && updateProfileRequest.isDone}
        onChangeLicensee={(e: React.ChangeEvent<HTMLInputElement>) => setLicensee(e.target.value)}
        onChangePhone={setPhone}
        country={profile.country}
        onClick={async () => {
          await Promise.all([
            dispatch<{ type: string }>(profileActions.updateProfile({ phone_number: phone }, profile.id)),
            dispatch<{ type: string }>(userSongActions.updateUserSong(userSong.id, { licensee })),
          ])
          dispatch(routerActions.push(`/pay/license/${licenseOrderId}/?step=pay`))
        }}
      />
    )
  } else if (step === 'pay' && userSong?.licensee && profile.phone_number) {
    mainContent = (
      <>
        <PaymentMethod />
        <PayButton
          disabled={!savedPaymentMethod}
          isLoading={paySampleLicenseOrderRequest.isLoading}
          onClick={() => dispatch(payActions.paySampleLicenseOrder(sampleLicenseOrder.id))}
          buttonText="Purchase license"
        />
      </>
    )
  }
  return (
    <CheckoutLayout
      sidebarContent={
        userSong && (
          <Summary.License
            userSong={userSong}
            song={sample?.song as Song}
            licenseInfo={licenseInfo}
            licensee={licensee || userSong.licensee}
            currency={currency}
            licensePrice={total_amount}
            vatPrice={vat_amount}
            vatRate={vat_rate}
            totalPrice={total_amount + vat_amount}
          />
        )
      }
    >
      {mainContent}
    </CheckoutLayout>
  )
}

export default LicensePay
